import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, apiFetchAllow404 } from './client'
import type { AIJobKind, AIJobStatusResponse, AIJobSubmitResponse, ApiError } from './types'
import { AI_JOB_POLL_INTERVAL_MS } from '../config'

function isApiError(e: unknown): e is ApiError {
  return (
    typeof e === 'object' &&
    e !== null &&
    'error' in e &&
    typeof (e as ApiError).error === 'object' &&
    (e as ApiError).error !== null &&
    'code' in (e as ApiError).error
  )
}

const TERMINAL = new Set(['succeeded', 'failed', 'timeout'])

export interface UseAsyncJobOptions {
  kind: AIJobKind
  submitPath: string
  pollIntervalMs?: number
}

export interface UseAsyncJobReturn<I, R> {
  submit: (input: I) => void
  /** Latest full job row from the server for this kind. */
  job: AIJobStatusResponse<R> | null
  isActive: boolean
  reset: () => void
  isSubmitting: boolean
}

/**
 * Async notes AI job flow: GET /ai-jobs?kind= polls one row; POST submit upserts that row.
 */
export function useAsyncJob<I, R>(opts: UseAsyncJobOptions): UseAsyncJobReturn<I, R> {
  const qc = useQueryClient()
  const pollMs = opts.pollIntervalMs ?? AI_JOB_POLL_INTERVAL_MS

  const jobQuery = useQuery({
    queryKey: ['ai-job', opts.kind],
    queryFn: () => apiFetchAllow404<AIJobStatusResponse<R>>(`/ai-jobs?kind=${opts.kind}`),
    refetchInterval: (query) => {
      const s = query.state.data?.status
      if (!s || TERMINAL.has(s)) return false
      return pollMs
    },
    staleTime: 0,
  })

  const reset = useCallback(() => {
    const j = qc.getQueryData<AIJobStatusResponse<R> | null>(['ai-job', opts.kind])
    if (j?.finished_at && TERMINAL.has(j.status)) {
      void apiFetch('/ai-jobs/seen', {
        method: 'POST',
        body: JSON.stringify({ kind: opts.kind, finished_at: j.finished_at }),
      }).catch(() => {})
    }
    qc.setQueryData(['ai-job', opts.kind], null)
    void qc.invalidateQueries({ queryKey: ['ai-job', opts.kind] })
  }, [qc, opts.kind])

  const submitMut = useMutation({
    mutationFn: async (input: I) => {
      try {
        return await apiFetch<AIJobSubmitResponse>(opts.submitPath, {
          method: 'POST',
          body: JSON.stringify(input),
        })
      } catch (e: unknown) {
        if (isApiError(e) && e.error.code === 'job_in_progress') {
          await qc.invalidateQueries({ queryKey: ['ai-job', opts.kind] })
          return { kind: opts.kind, status: 'pending' as const }
        }
        throw e
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-job', opts.kind] })
    },
  })

  const submit = useCallback(
    (input: I) => {
      submitMut.mutate(input)
    },
    [submitMut]
  )

  const job = jobQuery.data ?? null
  const isActive =
    !!job && (job.status === 'pending' || job.status === 'running')

  return {
    submit,
    job,
    isActive,
    reset,
    isSubmitting: submitMut.isPending,
  }
}
